import { SensorStatus } from "@sitescene/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Select } from "@/components/ui/select/select";
import { GeneralModal } from "@/features/modals/general-modal";

import type {
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";

import { useMapCoordinateInputs } from "../../hooks/useMapCoordinateInputs";
import { useUpdateSensor } from "../../hooks/useMapEntityUpdates";
import {
	createSensorMarkerFeature,
	isSensorMarkerFeature,
} from "../../map/markers/create-marker-feature";
import { getRefId } from "../../utils/location/installation-ids";
import { buildLocationMapOverlays } from "../../utils/location/location-map-features";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import { toTitleCase } from "../../utils/shared/strings";
import { CoordinateFields } from "./shared/coordinate-fields";
import { MapPlacementSection } from "./shared/map-placement-section";

const SENSOR_TYPE_OPTIONS = [
	{ id: "camera", label: "Camera" },
	{ id: "radar", label: "Radar" },
	{ id: "door", label: "Door" },
	{ id: "air_quality", label: "Air Quality" },
	{ id: "motion", label: "Motion" },
];

const SENSOR_STATUS_OPTIONS = [
	{ id: SensorStatus.Active, label: "Active" },
	{ id: SensorStatus.Warning, label: "Warning" },
	{ id: SensorStatus.Offline, label: "Offline" },
];

type EditSensorFormProps = {
	location: ShadowLocation;
	mapCenter?: [number, number] | null;
	onClose: () => void;
	/** Switch the URL-driven edit target when another pin is clicked. */
	onSelectSensor?: (sensorId: string) => void;
	sensor: ShadowSensor;
};

type EditSensorFormValues = {
	latitude: string;
	longitude: string;
	name: string;
	status: string;
	type: string;
	zone: string;
};

const getSensorFormValues = (
	sensor: ShadowSensor,
	zoneId: string,
): EditSensorFormValues => ({
	latitude: sensor.geolocation?.coordinates[1]?.toString() ?? "",
	longitude: sensor.geolocation?.coordinates[0]?.toString() ?? "",
	name: toTitleCase(sensor.name),
	status: sensor.status ?? SensorStatus.Active,
	type: sensor.type || "camera",
	zone: zoneId,
});

/**
 * Dashboard-style Manage Sensor modal for Shadow Realm list (and shared) edit.
 */
export const EditSensorForm = ({
	location,
	mapCenter,
	onClose,
	onSelectSensor,
	sensor,
}: EditSensorFormProps) => {
	const updateSensor = useUpdateSensor();
	const [submitError, setSubmitError] = useState<null | string>(null);

	const zones = useMemo(() => location.zones ?? [], [location.zones]);
	const sensorInstallationId = getRefId(sensor.installation);
	const currentZoneId = getRefId(sensor.zone) ?? "";

	const zoneOptions = useMemo(() => {
		const options = zones
			.filter((zone) => {
				if (!sensorInstallationId) return true;
				return getRefId(zone.installation) === sensorInstallationId;
			})
			.map((zone) => ({
				id: zone.id,
				label: toTitleCase(zone.name),
			}));

		if (
			currentZoneId &&
			!options.some((option) => option.id === currentZoneId)
		) {
			const currentZone = zones.find((zone) => zone.id === currentZoneId);
			options.unshift({
				id: currentZoneId,
				label: currentZone ? toTitleCase(currentZone.name) : "Current zone",
			});
		}

		return options;
	}, [currentZoneId, sensorInstallationId, zones]);

	const typeOptions = useMemo(() => {
		const options: { id: string; label: string }[] = [...SENSOR_TYPE_OPTIONS];
		if (sensor.type && !options.some((option) => option.id === sensor.type)) {
			options.push({
				id: sensor.type,
				label: toTitleCase(sensor.type),
			});
		}
		return options;
	}, [sensor.type]);

	const {
		control,
		formState: { errors, isValid },
		handleSubmit,
		reset,
		setValue,
	} = useForm<EditSensorFormValues>({
		defaultValues: getSensorFormValues(sensor, currentZoneId),
		mode: "onChange",
	});

	useEffect(() => {
		queueMicrotask(() => {
			reset(getSensorFormValues(sensor, getRefId(sensor.zone) ?? ""));
			setSubmitError(null);
		});
		// Reset form contents when the URL-driven sensor target changes — keep the
		// modal mounted (no remount key) so switching pins does not close/reopen.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- sensor.id
	}, [reset, sensor.id]);

	const selectedZoneId = useWatch({ control, name: "zone" }) ?? "";
	const watchedLatitude = useWatch({ control, name: "latitude" }) ?? "";
	const watchedLongitude = useWatch({ control, name: "longitude" }) ?? "";
	const watchedName = useWatch({ control, name: "name" }) ?? "";
	const watchedType = useWatch({ control, name: "type" }) ?? "camera";
	const watchedStatus =
		useWatch({ control, name: "status" }) ?? SensorStatus.Active;

	const selectedZone: ShadowZone | undefined = zones.find(
		(zone) => zone.id === selectedZoneId,
	);

	const {
		hasPartialCoordinates,
		mapFrameCenter,
		onMoveEnd,
		parsedCoordinates,
		viewportCenter,
	} = useMapCoordinateInputs({
		fallbackCenter: location.coordinates?.coordinates,
		latitude: watchedLatitude,
		longitude: watchedLongitude,
		mapCenter,
	});

	const zoneSensorMarkers = useMemo((): GeoJSON.Feature[] => {
		if (!selectedZoneId) return [];
		return (selectedZone?.sensors ?? [])
			.filter(
				(zoneSensor) =>
					zoneSensor.id !== sensor.id &&
					isValidCoordinatePair(zoneSensor.geolocation?.coordinates),
			)
			.map((zoneSensor) =>
				createSensorMarkerFeature({
					coordinates: zoneSensor.geolocation!.coordinates as [number, number],
					dimmed: true,
					id: zoneSensor.id,
					lastInputReceived: zoneSensor.lastInputReceived,
					locationId: location.id,
					name: zoneSensor.name,
					status: zoneSensor.status,
					type: zoneSensor.type,
					zoneId: selectedZoneId,
				}),
			);
	}, [location.id, selectedZone?.sensors, selectedZoneId, sensor.id]);

	const sensorMarker = useMemo((): GeoJSON.Feature | undefined => {
		if (!parsedCoordinates) return undefined;
		const feature = createSensorMarkerFeature({
			coordinates: parsedCoordinates,
			id: sensor.id,
			lastInputReceived: sensor.lastInputReceived,
			locationId: location.id,
			name: watchedName.trim() || sensor.name,
			status: (watchedStatus as SensorStatus) || sensor.status,
			type: watchedType || sensor.type,
			zoneId: selectedZoneId || undefined,
		});
		return {
			...feature,
			properties: {
				...feature.properties,
				dimmed: false,
				editable: true,
			},
		};
	}, [
		location.id,
		parsedCoordinates,
		selectedZoneId,
		sensor.id,
		sensor.lastInputReceived,
		sensor.name,
		sensor.status,
		sensor.type,
		watchedName,
		watchedStatus,
		watchedType,
	]);

	const mapMarkers = useMemo((): GeoJSON.Feature[] | undefined => {
		const markers = [...zoneSensorMarkers];
		if (sensorMarker) markers.push(sensorMarker);
		return markers.length > 0 ? markers : undefined;
	}, [sensorMarker, zoneSensorMarkers]);

	const mapOverlays = useMemo(() => {
		if (!selectedZoneId) return undefined;
		const excludeZoneIds = new Set(
			zones.map((zone) => zone.id).filter((id) => id !== selectedZoneId),
		);
		return buildLocationMapOverlays(location, { excludeZoneIds });
	}, [location, selectedZoneId, zones]);

	const setCoordinatePair = useCallback(
		([lng, lat]: [number, number]) => {
			setValue("latitude", String(lat), {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue("longitude", String(lng), {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[setValue],
	);

	const handleMapClick = useCallback(
		(coordinates: [number, number]) => {
			setCoordinatePair(coordinates);
		},
		[setCoordinatePair],
	);

	const handleMarkerClick = useCallback(
		(feature: GeoJSON.Feature) => {
			if (!isSensorMarkerFeature(feature) || feature.id == null) return;
			const featureId = String(feature.id);
			if (featureId === sensor.id) return;
			onSelectSensor?.(featureId);
		},
		[onSelectSensor, sensor.id],
	);

	const handleMarkerDragEnd = useCallback(
		(_feature: GeoJSON.Feature, coordinates: [number, number]) => {
			setCoordinatePair(coordinates);
		},
		[setCoordinatePair],
	);

	const handleUseMapCenter = useCallback(() => {
		if (!viewportCenter) return;
		setCoordinatePair(viewportCenter);
	}, [setCoordinatePair, viewportCenter]);

	const canSubmit =
		isValid &&
		zoneOptions.length > 0 &&
		!hasPartialCoordinates &&
		!updateSensor.isPending;

	const onSubmit = handleSubmit((data) => {
		if (!data.zone) {
			return;
		}

		const lat = parseFloat(data.latitude);
		const lng = parseFloat(data.longitude);
		const coordinates = isValidCoordinatePair([lng, lat])
			? ([lng, lat] as [number, number])
			: undefined;
		const hasPartial =
			(data.latitude.trim() !== "" || data.longitude.trim() !== "") &&
			!coordinates;

		if (hasPartial) {
			return;
		}

		setSubmitError(null);
		updateSensor.mutate(
			{
				body: {
					geolocation: coordinates ? { coordinates, type: "Point" } : null,
					name: data.name.trim(),
					status: data.status as SensorStatus,
					type: data.type || "camera",
					zone: data.zone,
				},
				sensorId: sensor.id,
			},
			{
				onError: () => {
					setSubmitError("Could not update sensor.");
				},
				onSuccess: () => {
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description="Used in alerts and location maps."
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			panelClassName="sm:max-w-160"
			submitDisabled={!canSubmit}
			submitText={updateSensor.isPending ? "Saving…" : "Save"}
			title="Manage Sensor"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Sensor Name"
							onBlur={field.onBlur}
							onChange={field.onChange}
							placeholder="Enter sensor name"
							ref={field.ref}
							size="md"
							value={field.value}
						/>
					)}
					rules={{
						required: "Sensor name is required",
						validate: (value) =>
							value.trim().length > 0 || "Sensor name is required",
					}}
				/>

				<Controller
					control={control}
					name="zone"
					render={({ field }) => (
						<Select
							errorMessage={errors.zone?.message}
							isDisabled={zoneOptions.length === 0}
							isInvalid={Boolean(errors.zone)}
							isRequired
							items={zoneOptions}
							label="Zone"
							onChange={(value) => {
								field.onChange(value ? String(value) : "");
							}}
							placeholder={
								zoneOptions.length === 0
									? "No zones available"
									: "Select a zone"
							}
							size="md"
							value={field.value || null}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
					rules={{ required: "Zone is required" }}
				/>

				<Controller
					control={control}
					name="type"
					render={({ field }) => (
						<Select
							items={typeOptions}
							label="Type"
							onChange={(value) => {
								field.onChange(value ? String(value) : "camera");
							}}
							placeholder="Select sensor type"
							size="md"
							value={field.value || "camera"}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
				/>

				<Controller
					control={control}
					name="status"
					render={({ field }) => (
						<Select
							items={[...SENSOR_STATUS_OPTIONS]}
							label="Status"
							onChange={(value) => {
								field.onChange(value ? String(value) : SensorStatus.Active);
							}}
							placeholder="Select status"
							size="md"
							value={field.value || SensorStatus.Active}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
				/>

				<hr className="my-1 border-secondary" />

				<div className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-primary">
						Map Coordinates
					</h3>
					<p className="text-sm text-tertiary">
						Optional. Click the map or drag the marker to place the sensor, or
						enter latitude and longitude manually. Other sensors in this zone
						appear dimmed — click one to switch which sensor you are editing.
						Clearing both fields removes the sensor from the map.
					</p>
				</div>

				<CoordinateFields control={control} errors={errors} />

				<MapPlacementSection
					center={mapFrameCenter}
					fitBoundsKey={selectedZoneId || undefined}
					hasPartialCoordinates={hasPartialCoordinates}
					markers={mapMarkers}
					markersInteractive={Boolean(mapMarkers?.length)}
					onMapClick={handleMapClick}
					onMarkerClick={onSelectSensor ? handleMarkerClick : undefined}
					onMarkerDragEnd={sensorMarker ? handleMarkerDragEnd : undefined}
					onMoveEnd={onMoveEnd}
					onUseMapCenter={handleUseMapCenter}
					overlays={mapOverlays}
					partialCoordinatesMessage="Enter both latitude and longitude to map this sensor."
					showUseMapCenter={Boolean(viewportCenter)}
				/>

				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
